const normalizeIteratee = (iteratee) => {
  if (typeof iteratee === "function") {
    return iteratee;
  }

  if (typeof iteratee === "string") {
    return (item) => item?.[iteratee];
  }

  if (Array.isArray(iteratee)) {
    const [key, value] = iteratee;

    return (item) => item?.[key] === value;
  }

  if (iteratee && typeof iteratee === "object") {
    return (item) =>
      Object.keys(iteratee).every((key) => item?.[key] === iteratee[key]);
  }

  return (item) => item;
};

export const isNil = (value) => value == null;

export const isArray = Array.isArray;

export const values = (obj) => {
  if (obj == null) {
    return [];
  }

  return Object.values(obj);
};

export const map = (collection, iteratee = (v) => v) => {
  if (collection == null) {
    return [];
  }

  const cb = normalizeIteratee(iteratee);

  if (Array.isArray(collection)) {
    return collection.map(cb);
  }

  return Object.keys(collection).map((key) => cb(collection[key], key));
};

export const filter = (collection, predicate = Boolean) => {
  if (collection == null) {
    return [];
  }

  const cb = normalizeIteratee(predicate);

  const arr = Array.isArray(collection)
    ? collection
    : Object.values(collection);

  return arr.filter(cb);
};

export const find = (collection, predicate) => {
  if (collection == null) {
    return undefined;
  }

  const cb = normalizeIteratee(predicate);

  const arr = Array.isArray(collection)
    ? collection
    : Object.values(collection);

  return arr.find(cb);
};

export const findIndex = (array, predicate) => {
  if (!Array.isArray(array)) {
    return -1;
  }

  return array.findIndex(normalizeIteratee(predicate));
};

export const includes = (collection, value) => {
  if (collection == null) {
    return false;
  }

  if (Array.isArray(collection) || typeof collection === "string") {
    return collection.includes(value);
  }

  return Object.values(collection).includes(value);
};

export const join = (array, separator = ",") => {
  if (!Array.isArray(array)) {
    return "";
  }

  return array.join(separator);
};

export const concat = (...arrays) => {
  return arrays.flatMap((arr) => (Array.isArray(arr) ? arr : [arr]));
};

export const slice = (array, start, end) => {
  if (!Array.isArray(array)) {
    return [];
  }

  return array.slice(start, end);
};

export const head = (array) => {
  if (!Array.isArray(array)) {
    return undefined;
  }

  return array[0];
};

export const last = (array) => {
  if (!Array.isArray(array)) {
    return undefined;
  }

  return array[array.length - 1];
};

export const nth = (array, index) => {
  if (!Array.isArray(array)) {
    return undefined;
  }

  return index >= 0 ? array[index] : array[array.length + index];
};

export const size = (value) => {
  if (value == null) {
    return 0;
  }

  if (Array.isArray(value) || typeof value === "string") {
    return value.length;
  }

  if (value instanceof Map || value instanceof Set) {
    return value.size;
  }

  if (typeof value === "object") {
    return Object.keys(value).length;
  }

  return 0;
};

export const isEmpty = (value) => {
  if (value == null) {
    return true;
  }

  if (Array.isArray(value) || typeof value === "string") {
    return value.length === 0;
  }

  if (value instanceof Map || value instanceof Set) {
    return value.size === 0;
  }

  if (typeof value === "object") {
    return Object.keys(value).length === 0;
  }

  return true;
};

export const toPairs = (obj) => {
  if (obj == null) {
    return [];
  }

  return Object.entries(obj);
};

export const mapValues = (obj, iteratee = (v) => v) => {
  if (obj == null) {
    return {};
  }

  const cb = normalizeIteratee(iteratee);

  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, cb(value, key)]),
  );
};

export const keyBy = (array, key) => {
  if (!Array.isArray(array)) {
    return {};
  }

  return Object.fromEntries(
    array.map((item) => [
      typeof key === "function" ? key(item) : item?.[key],
      item,
    ]),
  );
};

export const reduce = (collection, iteratee, accumulator) => {
  if (collection == null) {
    return accumulator;
  }

  const arr = Array.isArray(collection)
    ? collection
    : Object.values(collection);

  return arr.reduce(iteratee, accumulator);
};

export const unset = (obj, path) => {
  if (obj == null || typeof path !== "string") {
    return false;
  }

  const keys = path.split(".");
  const lastKey = keys.pop();

  const target = keys.reduce((acc, key) => acc?.[key], obj);

  if (target && lastKey in target) {
    delete target[lastKey];
    return true;
  }

  return false;
};

export const intersection = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b)) {
    return [];
  }

  return a.filter((item) => b.includes(item));
};

export const sortBy = (array, iteratee) => {
  if (!Array.isArray(array)) {
    return [];
  }

  return [...array].sort((a, b) => {
    const av = typeof iteratee === "function" ? iteratee(a) : a?.[iteratee];

    const bv = typeof iteratee === "function" ? iteratee(b) : b?.[iteratee];

    if (av > bv) return 1;
    if (av < bv) return -1;

    return 0;
  });
};

export const orderBy = (array, iteratees = [], orders = []) => {
  if (!Array.isArray(array)) {
    return [];
  }

  return [...array].sort((a, b) => {
    for (let i = 0; i < iteratees.length; i++) {
      const iteratee = iteratees[i];
      const order = orders[i] || "asc";

      const av = typeof iteratee === "function" ? iteratee(a) : a?.[iteratee];

      const bv = typeof iteratee === "function" ? iteratee(b) : b?.[iteratee];

      if (av > bv) {
        return order === "desc" ? -1 : 1;
      }

      if (av < bv) {
        return order === "desc" ? 1 : -1;
      }
    }

    return 0;
  });
};

export const isEqual = (a, b) => {
  if (a === b) {
    return true;
  }

  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
};

export const cloneDeep = (value) => {
  try {
    if (
      typeof window !== "undefined" &&
      typeof window.structuredClone === "function"
    ) {
      return window.structuredClone(value);
    }

    return JSON.parse(JSON.stringify(value));
  } catch {
    return value;
  }
};

export const merge = (...objects) => {
  const result = {};

  for (const obj of objects) {
    if (obj == null || typeof obj !== "object") {
      continue;
    }

    for (const key in obj) {
      if (
        typeof obj[key] === "object" &&
        obj[key] !== null &&
        !Array.isArray(obj[key])
      ) {
        result[key] = merge(result[key] || {}, obj[key]);
      } else {
        result[key] = obj[key];
      }
    }
  }

  return result;
};
